<template lang="pug">
.briq-form-element.briq-rich-editor
  label {{ label }}
  briq-required-and-optional-markers(:isRequired="this.$attrs.required" :isDisabled="this.$attrs.disabled || this.$attrs.readonly" :hideOptionalMarker="hideOptionalMarker" :label="label")
  briq-tooltip(:help="help")
  .input-holder
    div(ref='editorEl')
      .input.type-standard(v-if="editor" v-bind="$attrs" :class="{ 'input-focused': editorFocused }")
        .justify-buttons
          div
            briq-button(
              variant="secondary"
              @click="toggleBold"
              :class="{ 'is-active': editor && editor.isActive('bold') }"
              :disabled="this.$attrs.disabled || this.$attrs.readonly"
              )
              img(src="../../assets/icons/svg/bold-icon.svg")
            briq-button(
              variant="secondary"
              @click="toggleItalic"
              :class="{ 'is-active': editor && editor.isActive('italic') }"
              :disabled="this.$attrs.disabled || this.$attrs.readonly"
              )
              img(src="../../assets/icons/svg/italic-icon.svg")
            briq-button(
              variant="secondary"
              @click="toggleUnderline"
              :class="{ 'is-active': editor && editor.isActive('underline') }"
              :disabled="this.$attrs.disabled || this.$attrs.readonly"
              )
              img(src="../../assets/icons/svg/underline-icon.svg")
            briq-button(
              variant="secondary"
              id="link"
              @click="openDialog"
              :class="{ 'is-active': editor && editor.isActive('link') }"
              :disabled="this.$attrs.disabled || this.$attrs.readonly"
              )
              img(src="../../assets/icons/svg/link-icon.svg")
            briq-button(
              variant="secondary"
              @click="toggleBulletList"
              :class="{ 'is-active': editor && editor.isActive('bulletList') }"
              :disabled="this.$attrs.disabled || this.$attrs.readonly"
              )
              img(src="../../assets/icons/svg/list-icon.svg")
            BriqRichEditorLinkPopup(
              v-show="showDialog"
              @closeDialog="closeDialog"
              @saveLink="saveLink"
              @editLink="editLink"
              @removeLink="removeLink"
              :inputLinkValue="inputLinkValue"
              :showSaveForm="showSaveForm"
              )
          div(v-if="allowHTMLEditor && isSystemAdmin")
            briq-button(
              variant="secondary"
              @click="toggleHTMLView"
              data-test-id="html-toggle"
              :class="{ 'is-active': showHTMLCode }"
              :disabled="this.$attrs.disabled || this.$attrs.readonly"
              )
              img(src="../../assets/icons/svg/code-icon.svg")
        editor-content(:editor="editor")
    .html-input(v-if="showHTMLCode && allowHTMLEditor")
      textarea(data-test-id="html-editor" :value="innerValue" @input="emitRawHTML($event.target.value)")
  .validation-message(v-if="error") {{ error }}
  .validation-message(v-if="sanitizeError") {{ sanitizeError }}
</template>

<script>
import { mapGetters } from 'vuex'
import * as DOMPurify from 'dompurify';
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import BriqButton from '../components-global/BriqButton.vue'
import BriqRichEditorLinkPopup from '../components-global/BriqRichEditorLinkPopup.vue'


export default {
  name: 'BriqRichEditorTextarea',
  components: { BriqButton, BriqRichEditorLinkPopup, EditorContent },
  props: {
    label: String,
    name: String,
    hideOptionalMarker: {
      type: Boolean,
      default: false,
    },
    help: String,
    error: String,
    value: String,
    allowHTMLEditor: {
      type: Boolean,
      required: false,
      default: false
    },
    allowTemplateSyntax: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  inheritAttrs: false,
  data() {
    return {
      innerValue: '',
      inputLinkValue: '',
      showDialog: false,
      showSaveForm: true,
      editor: null,
      showHTMLCode: false,
      editorFocused: false,
      HTMLEditorFocused: false,
      sanitizeError: ''
    }
  },
  computed: {
    ...mapGetters('briqState', ['userHasPermission']),
    isSystemAdmin() {
      return this.userHasPermission('Frontend_Application_Enable_Html_Editor')
    }
  },
  methods: {
    toggleHTMLView() {
      this.showHTMLCode = !this.showHTMLCode
    },
    toggleBold() {
      this.editor && this.editor?.chain().focus().toggleBold().run()
    },
    toggleItalic() {
      this.editor && this.editor?.chain().focus().toggleItalic().run()
    },
    toggleUnderline() {
      this.editor && this.editor?.chain().focus().toggleUnderline().run()
    },
    toggleBulletList() {
      this.editor && this.editor?.chain().focus().toggleBulletList().run()
    },
    openDialog() {
      this.showDialog = true
      const previousUrl = this.editor.getAttributes('link').href
      this.inputLinkValue = previousUrl
      this.showSaveForm = !previousUrl
    },
    closeDialog() {
      this.showDialog = false
      this.inputLinkValue = ''
    },
    saveLink(link) {
      this.editor
        ?.chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: link })
        .run()
      this.closeDialog()
    },
    editLink() {
      this.showSaveForm = true
    },
    removeLink() {
      this.editor
        ?.chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .run()
      this.closeDialog()
    },
    sanitizeHTML(input) {
      return DOMPurify.sanitize(input, {
          USE_PROFILES: {
            html: true
          },
          ALLOWED_TAGS: ['div'],
          ADD_ATTR: ['target', 'rel'],
          SAFE_FOR_TEMPLATES: !this.allowTemplateSyntax,
        })
    },
    compareSanitizedHTML(input) {
      const sanitized = this.sanitizeHTML(input)
      
      if (input !== sanitized) {
        const removed = DOMPurify.removed.map((item) => item.attribute?.name ? `${item.attribute?.name}=${item.attribute?.nodeValue}` : item.element?.outerHTML)
        if (removed?.length > 0) {
          if (!this.sanitizeError) this.$emit('hasHTMLError', true)
          this.sanitizeError = `Your HTML could be vulnerable through these elements: ${removed.join(' | ')}`
        } else {
          if (this.sanitizeError) this.$emit('hasHTMLError', false)
          this.sanitizeError = ''
        }
      }
      if (!input && !sanitized) {
        if (this.sanitizeError) this.$emit('hasHTMLError', false)
        this.sanitizeError = ''
      }
    },
    emitRawHTML(input) {
      if (input || input === '') {
        this.$emit('input', input)
      }
    }
  },
  watch: {
    value(newVal) {
      this.innerValue = newVal
    },
    innerValue(newVal) {
      this.compareSanitizedHTML(newVal)
      if (this.editor.getHTML() !== newVal) {
        this.editor?.commands.setContent(newVal, false, { preserveWhitespace: 'full' })
      }
    },
  },
  mounted() {
    this.innerValue = this.value

    const editorEl = this.$refs.editorEl
    if (editorEl) {
      editorEl.addEventListener('click', (e) => {
        const targetType = e.target.nodeName
        if (targetType === 'A') {
          this.openDialog()
        }
      })
    }

    this.editor = new Editor({
      content: this.innerValue,
      extensions: [
        StarterKit,
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: false,
          HTMLAttributes: {
            rel: 'noopener noreferrer',
          },
        }),
      ],
      parseOptions: {
        preserveWhitespace: 'full',
      },
      editorProps: {
        handleDOMEvents: {
          keydown(view, event) {
            if (event.key === 'Tab') {
              event.preventDefault()
              // insert 4 empty spaces when tab is pressed
              this.editor?.commands.insertContent('    ')
            }
            return false
          },
        },
      },
      autofocus: false,
      editable: true,
      onUpdate: () => {
        this.$emit('input', this.editor.getHTML())
      },
      onFocus: () => {
        this.editorFocused = true
      },
      onBlur: () => {
        this.editorFocused = false
      },
    })
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  created() {
    if (this.value) {
      this.compareSanitizedHTML(this.value)
    }
  }
}
</script>

<style scoped lang="stylus">
.input
  position relative
  display block
  width 100%
  border solid 1px #ebebeb
  border-radius 3px
  background white
  color #2c333a
  padding 8px 8px 4px

  &[disabled], &[readonly]
    background #eee

  &[disabled]
    cursor not-allowed

  &::placeholder
    color fade(@color-text, 40%)

  &:hover
    border-color #d3d3d3

.input-focused
  outline 0
  border-color #009BFF !important
  box-shadow 0 2px 4px 0 rgba(0, 0, 0, .15)

button
  margin-right: 5px
.justify-buttons
  display: flex
  justify-content: space-between
  height: auto

textarea
  margin-top: 12px
  width: 100%
  min-height: 200px
  border solid 1px #ebebeb
  border-radius 3px
  background white
  color #2c333a
  padding 8px 8px 4px

  &[disabled], &[readonly]
    background #eee

  &[disabled]
    cursor not-allowed

  &::placeholder
    color fade(@color-text, 40%)

  &:hover
    border-color #d3d3d3

textarea:focused
  outline 0
  border-color #009BFF
  box-shadow 0 2px 4px 0 rgba(0, 0, 0, .15)
</style>

<style lang="stylus">
.briq-rich-editor .ql-container.ql-snow
  border 0

.briq-rich-editor .ql-toolbar.ql-snow
  border 0

.briq-rich-editor .ql-snow .ql-toolbar button, .briq-rich-editor .ql-snow.ql-toolbar button
  border-radius 2px
  box-shadow 0 1px 2px rgba(0,0,0,.22)
  vertical-align middle
  height auto
  width auto
  padding 9px 15px 6px
  margin-right 5px
  margin-bottom 4px

.briq-rich-editor .ql-editor
  font-size 12px
  line-height 16px

.ProseMirror
  min-height 200px
  font-size 12px
  line-height 16px
  padding 12px 15px
  height: 100%

  ul
    padding-left 30px

  a
    color: #0d6efd

  a:hover
    text-decoration: underline

.ProseMirror:focus
  outline none
</style>
